import { useCallback, useMemo, useState } from 'react';
import { Product } from 'shared/types/product/Product';
import Button from 'components/commercetools-ui/atoms/button';
import useTrack from 'components/commercetools-ui/organisms/product/product-details/hooks/useTrack';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { PDP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import { useCart } from 'frontastic';

export interface ProductBtnProps {
  ctaLabel: string;
  ctaAction: 'add-to-cart';
  ctaSize: 'medium' | 'large';
  ctaStyle: 'primary' | 'primary-red' | 'primary-orange' | 'secondary' | 'text';
  ctaDataAnalytics?: string;
}

export interface CtaProductProps {
  product: Product;
  listBtn: ProductBtnProps[];
  alignment: 'left' | 'right' | 'center';
  bgColor: string;
  sectionId?: string;
}

const generateButtonClasses = (buttonVariant: string, ctaSize: string) => {
  const buttonSizeClasses = ctaSize === 'medium' ? 'h-36 md:px-12 md:py-11' : 'h-40 md:px-20 md:py-13';
  const buttonCommonClasses = `w-full md:w-auto flex items-center justify-center rounded-md ${buttonSizeClasses}`;

  const variantClasses: Record<string, string> = {
    primary: `${buttonCommonClasses} bg-padi-blue hover:bg-blue-700 text-neutral-150`,
    'primary-red': `${buttonCommonClasses} bg-padi-red-darker hover:bg-padi-red-dark text-white`,
    'primary-orange': `${buttonCommonClasses} bg-[#E7532C] hover:bg-[#c84524] text-white`,
    secondary: `${buttonCommonClasses} bg-neutral-100 hover:bg-neutral-400 border-[1px] border-padi-blue text-padi-blue`,
  };

  return variantClasses[buttonVariant] || variantClasses.primary;
};

const generateTextLinkClasses = (ctaSize: string, bgColor: string) => {
  const buttonSizeClasses = ctaSize === 'medium' ? 'h-36 text-sm' : 'h-40 text-base';
  const textColor = ['white', 'padi-blue-light', 'padi-gray-light', 'padi-gray-lighter'].includes(bgColor)
    ? 'text-padi-blue-darker'
    : 'text-white';
  return `flex justify-center items-center font-bold ${textColor} ${buttonSizeClasses}`;
};

const ActionButton: React.FC<{
  btn: ProductBtnProps;
  product: Product;
  bgColor: string;
}> = ({ btn, product, bgColor }) => {
  const { addItem } = useCart();
  const { show } = useAddToCartOverlay();
  const { trackAddToCart } = useTrack({ product });
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  // Mirror the PDP add-to-cart button: it always renders data-analytics="PDP: Product Added To Cart",
  // which the GTM container keys on for the GA4 add_to_cart event. Only fall back to it for the
  // add-to-cart action so a CMS-provided value still wins.
  const dataAnalytics = useMemo(
    () => btn.ctaDataAnalytics || (btn.ctaAction === 'add-to-cart' ? PDP_PRODUCT_ADDED_TO_CART : undefined),
    [btn.ctaDataAnalytics, btn.ctaAction],
  );

  const handleClick = useCallback(async () => {
    if (btn.ctaAction === 'add-to-cart') {
      const variant = product.variants?.[0];
      if (!variant) return;

      setLoading(true);
      addItem(product, variant, 1).then(() => {
        setLoading(false);
        setAdded(true);
        setTimeout(() => setAdded(false), 1000);
        show(product, variant, 1);
      });
      trackAddToCart(1);
    }
  }, [btn.ctaAction, product, addItem, show, trackAddToCart]);

  if (btn.ctaStyle === 'text') {
    return (
      <div className={generateTextLinkClasses(btn.ctaSize, bgColor)}>
        <button type="button" onClick={handleClick} data-analytics={dataAnalytics}>
          {btn.ctaLabel}
        </button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      className={generateButtonClasses(btn.ctaStyle, btn.ctaSize)}
      onClick={handleClick}
      loading={loading}
      added={added}
      dataAnalytics={dataAnalytics}
    >
      {btn.ctaLabel}
    </Button>
  );
};

const CtaProduct: React.FC<CtaProductProps> = ({ product, listBtn, alignment, bgColor, sectionId }) => {
  const alignmentClasses = useMemo(() => {
    const alignmentMapping = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
    };
    return alignmentMapping[alignment] || '';
  }, [alignment]);

  const ctaClasses = useMemo(() => {
    const areAllCtasTextLinks = listBtn.every((cta) => cta.ctaStyle === 'text');

    return `mx-auto max-w-7xl px-24 lg:px-32 flex flex-col md:flex-row ${
      areAllCtasTextLinks ? 'gap-0 md:gap-16' : 'gap-16'
    }  ${alignmentClasses}`;
  }, [listBtn, alignmentClasses]);

  const bgColorClasses: Record<string, string> = {
    white: 'bg-white',
    'padi-blue-light': 'bg-padi-blue-light',
    'padi-gray-light': 'bg-padi-gray-light',
    'padi-gray-lighter': 'bg-padi-gray-lighter',
    'padi-gray-darkest': 'bg-padi-gray-darkest',
    black: 'bg-black',
  };

  return (
    <div className={bgColorClasses[bgColor] || 'bg-white'} id={sectionId}>
      <div className={ctaClasses}>
        {listBtn.map((btn, index) => (
          <ActionButton key={index} btn={btn} product={product} bgColor={bgColor} />
        ))}
      </div>
    </div>
  );
};

export default CtaProduct;
